import React, { useState } from 'react';
import { useForm } from '@inertiajs/react';
import { Mail, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/design-system/Badge';
import { Avatar } from '@/components/design-system';

interface Member {
  id: string;
  user: {
    id: number;
    name: string;
    email: string;
    avatar_url: string | null;
  };
  role: string;
  is_owner: boolean;
  joined_at: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  invited_by: {
    id: number;
    email: string;
  };
  expires_at: string;
  created_at: string;
}

interface MembersSectionProps {
  organizationSlug: string;
  members: Member[];
  pendingInvitations: PendingInvitation[];
  canManage: boolean;
  onRemoveMember: (member: Member) => void;
  onCancelInvitation: (invitation: PendingInvitation) => void;
  onChangeRole: (membershipId: string, newRole: string) => void;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Proprietário',
  editor: 'Editor',
  viewer: 'Visualizador',
};

const ROLE_BADGE_TONES: Record<string, 'purple' | 'info' | 'success'> = {
  owner: 'purple',
  editor: 'info',
  viewer: 'success',
};

export function MembersSection({
  organizationSlug,
  members,
  pendingInvitations,
  canManage,
  onRemoveMember,
  onCancelInvitation,
  onChangeRole,
}: MembersSectionProps) {
  const [showInviteForm, setShowInviteForm] = useState(false);

  const { data: inviteData, setData: setInviteData, post: sendInvite, processing: inviteProcessing, errors: inviteErrors } = useForm({
    email: '',
    role: 'editor' as 'editor' | 'viewer',
  });

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendInvite(`/dashboard/organizations/${organizationSlug}/invitations`, {
      onSuccess: () => {
        setShowInviteForm(false);
        setInviteData('email', '');
        setInviteData('role', 'editor');
      }
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle><h2 className="text-lg font-semibold">Membros da Organização</h2></CardTitle>
        {canManage && (
          <CardAction>
            <Button variant="outline" size="sm" onClick={() => setShowInviteForm(true)}>
              Convidar Membro
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {showInviteForm && (
          <div className="mb-6 pb-6 border-b space-y-4">
            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email do novo membro</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData('email', e.target.value)}
                  placeholder="usuario@exemplo.com"
                  required
                  aria-invalid={!!inviteErrors.email}
                />
                {inviteErrors.email && (
                  <p className="text-sm text-destructive">{inviteErrors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Cargo</Label>
                <Select value={inviteData.role} onValueChange={(value) => setInviteData('role', value as 'editor' | 'viewer')}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Visualizador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={inviteProcessing} aria-busy={inviteProcessing}>
                  {inviteProcessing && <Loader2 className="animate-spin" />}
                  {inviteProcessing ? 'Enviando...' : 'Enviar Convite'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowInviteForm(false);
                    setInviteData('email', '');
                    setInviteData('role', 'editor');
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="divide-y">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between py-3"
              data-testid={`member-row-${member.user.id}`}
            >
              <div className="flex items-center gap-3">
                <Avatar src={member.user.avatar_url || undefined} name={member.user.name} size="sm" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{member.user.name}</span>
                    <Badge tone={ROLE_BADGE_TONES[member.role] || 'info'} size="small">
                      {ROLE_LABELS[member.role] || member.role}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {member.user.email} · Entrou em {new Date(member.joined_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
              {canManage && !member.is_owner && (
                <div className="flex items-center gap-3">
                  <Select value={member.role} onValueChange={(value) => onChangeRole(member.id, value)}>
                    <SelectTrigger size="sm" aria-label={`Alterar cargo de ${member.user.email}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Visualizador</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveMember(member)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remover
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>

        {pendingInvitations.length > 0 && (
          <div className="mt-2 pt-4 border-t">
            <h3 className="text-sm font-medium mb-3 text-muted-foreground">Convites Pendentes</h3>
            <div className="divide-y">
              {pendingInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between py-3"
                  data-testid={`pending-invitation-${invitation.email}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{invitation.email}</span>
                        <Badge tone="warning" size="small">Pendente</Badge>
                        <Badge tone={ROLE_BADGE_TONES[invitation.role] || 'info'} size="small">
                          {ROLE_LABELS[invitation.role] || invitation.role}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Convidado por {invitation.invited_by.email} · Expira em {new Date(invitation.expires_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCancelInvitation(invitation)}
                      className="text-destructive hover:text-destructive"
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
